import { IPolicyOptionItem } from "../types/builder";
import { PolicyFieldOptionSource } from "../types/policyForm";

const ENCODED_ORG_ID_PLACEHOLDER = "{encoded_organization_id}";

export interface OptionSourceUrlContext {
  encodedOrganizationId: string | null;
}

export function resolveOptionSourceUrl(
  api: string,
  context: OptionSourceUrlContext,
): string | null {
  if (!api) {
    return null;
  }

  if (
    api.includes(ENCODED_ORG_ID_PLACEHOLDER) &&
    !context.encodedOrganizationId
  ) {
    return null;
  }

  return api.replaceAll(
    ENCODED_ORG_ID_PLACEHOLDER,
    context.encodedOrganizationId ?? "",
  );
}

export function buildOptionRequestParams(
  optionSource: Pick<
    PolicyFieldOptionSource,
    "defaultParams" | "supportsSearch" | "searchParam"
  >,
  searchQuery?: string,
): Record<string, unknown> {
  const params: Record<string, unknown> = {
    ...(optionSource.defaultParams ?? {}),
  };

  const normalizedQuery = searchQuery?.trim();
  if (
    normalizedQuery &&
    optionSource.supportsSearch &&
    optionSource.searchParam
  ) {
    params[optionSource.searchParam] = normalizedQuery;
  }

  return params;
}

export function extractOptionItemsRaw(data: unknown): unknown[] {
  if (Array.isArray(data)) {
    return data;
  }

  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    if (Array.isArray(record.results)) {
      return record.results;
    }
    if (Array.isArray(record.data)) {
      return record.data;
    }
  }

  return [];
}

export function normalizeOptionItems(
  raw: unknown,
  optionSource: Pick<PolicyFieldOptionSource, "labelField" | "valueField">,
): IPolicyOptionItem[] {
  return extractOptionItemsRaw(raw)
    .map((item) => {
      if (item && typeof item === "object") {
        const record = item as Record<string, unknown>;
        const label = String(record[optionSource.labelField] ?? "");
        const value = String(record[optionSource.valueField] ?? "");
        return { label: `${label} - ${value}`, value };
      }

      return {
        label: String(item ?? ""),
        value: String(item ?? ""),
      };
    })
    .filter((option) => option.value);
}
